import React from "react";
import { Lock, MapPin, Check, ArrowRight } from "lucide-react";

const d = (s) => ({ animationDelay: `${s}s` });
const fs = (cqw) => ({ fontSize: `${cqw}cqw` });

const Backdrop = ({ image, tint }) => (
  <>
    {image ? <div className="igr-bg" style={{ backgroundImage: `url(${image})` }} /> : <div className="absolute inset-0" style={{ background: tint || "#0e0e0e" }} />}
    {image && <div className="igr-shade" />}
    <div className="igr-grain" />
  </>
);

const Kicker = ({ children, delay = 0.1, color = "#ff4500" }) => (
  <div className="igr-in igr-mono self-start font-bold px-[3cqw] py-[1.4cqw]" style={{ ...d(delay), ...fs(3.4), background: color }}>
    {children}
  </div>
);

export const HookScene = ({ s }) => (
  <div className="igr-scene justify-end">
    <Backdrop image={s.image} tint={s.tint} />
    <div className="relative flex flex-col gap-[4cqw]">
      {s.kicker && <Kicker>{s.kicker}</Kicker>}
      <div className="igr-display igr-in" style={{ ...d(0.35), ...fs(s.size || 13) }}>{s.headline}</div>
      {s.sub && <div className="igr-in text-white/85 font-medium" style={{ ...d(0.9), ...fs(4.6) }}>{s.sub}</div>}
    </div>
  </div>
);

export const PriceScene = ({ s }) => (
  <div className="igr-scene justify-center">
    <Backdrop tint="#fffbf2" />
    <div className="relative">
      <div className="igr-pop bg-white text-[#0e0e0e] shadow-[0_3cqw_0_0_#0e0e0e] border-[0.6cqw] border-[#0e0e0e]" style={d(0.1)}>
        <div className="h-[48cqw] bg-cover bg-center" style={{ backgroundImage: `url(${s.image})` }} />
        <div className="p-[5cqw]">
          <div className="igr-display" style={fs(7)}>{s.name}</div>
          <div className="flex items-center gap-[1.5cqw] text-[#666] mt-[1.5cqw]" style={fs(3.6)}><MapPin style={{ width: "3.6cqw", height: "3.6cqw" }} />{s.region}</div>
          <div className="mt-[5cqw] flex items-end justify-between">
            <div>
              <div className="igr-mono text-[#888]" style={fs(2.8)}>Price per night</div>
              <div className={`igr-display text-[#ff4500] ${s.reveal ? "igr-unblur" : ""}`} style={{ ...fs(13), ...(s.reveal ? d(0.6) : { filter: "blur(3cqw)" }) }}>{s.price}</div>
            </div>
            {!s.reveal && (
              <div className="igr-pop igr-shake bg-[#0e0e0e] text-white flex items-center gap-[1.5cqw] px-[3cqw] py-[2cqw] igr-mono font-bold" style={{ ...d(0.7), ...fs(3) }}>
                <Lock style={{ width: "3.4cqw", height: "3.4cqw" }} /> Members only
              </div>
            )}
          </div>
        </div>
      </div>
      {s.reveal && (
        <div className="igr-stamp absolute -top-[5cqw] -right-[3cqw] bg-[#0a7f3f] text-white igr-mono font-black px-[4cqw] py-[2.5cqw]" style={{ ...d(1.1), ...fs(4.4) }}>Unlocked</div>
      )}
      {s.caption && <div className="igr-in text-[#0e0e0e] font-bold text-center mt-[7cqw]" style={{ ...d(1.3), ...fs(5.2) }}>{s.caption}</div>}
    </div>
  </div>
);

export const ListScene = ({ s }) => (
  <div className="igr-scene justify-center">
    <Backdrop image={s.image} tint={s.tint || "#ff4500"} />
    <div className="relative">
      {s.kicker && <Kicker color="#0e0e0e">{s.kicker}</Kicker>}
      <div className="igr-display igr-in mt-[4cqw] mb-[6cqw]" style={{ ...d(0.2), ...fs(10) }}>{s.headline}</div>
      <ul className="flex flex-col gap-[3cqw]">
        {s.items.map((it, i) => (
          <li key={it} className="igr-in flex items-center gap-[3cqw] bg-white/15 backdrop-blur-md border border-white/25 px-[4cqw] py-[3cqw] font-bold" style={{ ...d(0.6 + i * 0.35), ...fs(5) }}>
            <span className="bg-white text-[#0e0e0e] rounded-full flex items-center justify-center shrink-0" style={{ width: "6cqw", height: "6cqw" }}>
              <Check style={{ width: "4cqw", height: "4cqw" }} strokeWidth={3} />
            </span>
            {it}
          </li>
        ))}
      </ul>
    </div>
  </div>
);

export const CounterScene = ({ s }) => (
  <div className="igr-scene justify-center items-center text-center">
    <Backdrop image={s.image} tint="#0e0e0e" />
    <div className="relative">
      {s.kicker && <div className="igr-in igr-mono text-[#ff4500] font-bold mb-[3cqw]" style={{ ...d(0.1), ...fs(3.8) }}>{s.kicker}</div>}
      <div className="igr-display igr-pop" style={{ ...d(0.3), ...fs(s.size || 24) }}>{s.value}</div>
      <div className="igr-in font-bold mt-[3cqw]" style={{ ...d(0.8), ...fs(6) }}>{s.label}</div>
      {s.sub && <div className="igr-in text-white/70 mt-[3cqw]" style={{ ...d(1.3), ...fs(4.2) }}>{s.sub}</div>}
    </div>
  </div>
);

export const DotsScene = ({ s }) => {
  const total = 180;
  const lit = new Set(Array.from({ length: s.lit || 3 }, (_, i) => (i * 61 + 17) % total));
  return (
    <div className="igr-scene justify-center">
      <Backdrop tint="#0e0e0e" />
      <div className="relative">
        <div className="igr-display igr-in mb-[6cqw]" style={{ ...d(0.1), ...fs(9) }}>{s.headline}</div>
        <div className="grid grid-cols-[repeat(15,1fr)] gap-[1.4cqw]">
          {Array.from({ length: total }, (_, i) => (
            <span key={i} className={`igr-pop rounded-full aspect-square ${lit.has(i) ? "bg-[#ff4500] igr-pulse" : "bg-white/15"}`} style={d(0.3 + (i % 15) * 0.03 + Math.floor(i / 15) * 0.02)} />
          ))}
        </div>
        <div className="igr-in font-bold mt-[6cqw]" style={{ ...d(1.4), ...fs(5.4) }}>{s.caption}</div>
        {s.sub && <div className="igr-in text-white/65 mt-[2cqw]" style={{ ...d(1.8), ...fs(4) }}>{s.sub}</div>}
      </div>
    </div>
  );
};

export const MapScene = ({ s }) => (
  <div className="igr-scene justify-center">
    <Backdrop tint="#0b2a1a" />
    <div className="absolute inset-0 opacity-25" style={{ backgroundImage: "linear-gradient(#ffffff22 1px, transparent 1px), linear-gradient(90deg, #ffffff22 1px, transparent 1px)", backgroundSize: "9cqw 9cqw" }} />
    <div className="relative">
      <Kicker color="#0a7f3f">{s.kicker}</Kicker>
      <div className="igr-display igr-in mt-[4cqw]" style={{ ...d(0.25), ...fs(11) }}>{s.headline}</div>
      <div className="relative h-[90cqw] mt-[4cqw]">
        {s.pins.map((p, i) => (
          <div key={p.label} className="igr-pop absolute flex flex-col items-center" style={{ left: `${p.x}%`, top: `${p.y}%`, ...d(0.7 + i * 0.3) }}>
            <div className="bg-white text-[#0e0e0e] font-bold px-[2cqw] py-[1cqw] whitespace-nowrap mb-[1cqw]" style={fs(3.2)}>{p.label}</div>
            <MapPin className="text-[#ff4500] igr-pulse" style={{ width: "8cqw", height: "8cqw" }} fill="#ff4500" stroke="#fff" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

export const ChatScene = ({ s }) => (
  <div className="igr-scene justify-center">
    <Backdrop tint="#fff4ee" />
    <div className="relative flex flex-col gap-[3cqw]">
      <div className="igr-in igr-mono text-[#ff4500] font-bold" style={{ ...d(0.1), ...fs(3.6) }}>{s.kicker}</div>
      {s.messages.map((m, i) => (
        <div key={i} className={`igr-pop max-w-[82%] px-[4cqw] py-[3cqw] rounded-[5cqw] ${m.me ? "self-end bg-[#ff4500] text-white rounded-br-[1cqw]" : "self-start bg-white text-[#0e0e0e] border border-[#e5e5e5] rounded-bl-[1cqw]"}`} style={{ ...d(0.4 + i * 0.55), transformOrigin: m.me ? "right bottom" : "left bottom" }}>
          {!m.me && <div className="igr-mono text-[#888] mb-[1cqw]" style={fs(2.6)}>{m.who}</div>}
          <div className="font-semibold" style={fs(4.6)}>{m.text}</div>
        </div>
      ))}
    </div>
  </div>
);

export const PhotosScene = ({ s }) => (
  <div className="igr-scene justify-center">
    <Backdrop tint="#0e0e0e" />
    <div className="relative">
      <div className="igr-display igr-in mb-[5cqw]" style={{ ...d(0.1), ...fs(10) }}>{s.headline}</div>
      <div className="grid grid-cols-2 gap-[2.5cqw]">
        {s.images.map((img, i) => (
          <div key={img} className="igr-pop aspect-square bg-cover bg-center border-[1cqw] border-white" style={{ backgroundImage: `url(${img})`, ...d(0.4 + i * 0.25), transform: `rotate(${i % 2 ? 3 : -3}deg)` }} />
        ))}
      </div>
      {s.caption && <div className="igr-in font-bold mt-[6cqw]" style={{ ...d(1.6), ...fs(5) }}>{s.caption}</div>}
    </div>
  </div>
);

export const CtaScene = ({ s }) => (
  <div className="igr-scene justify-center items-center text-center">
    <Backdrop image={s.image} tint="#ff4500" />
    <div className="relative flex flex-col items-center">
      <div className="igr-display igr-in" style={{ ...d(0.1), ...fs(s.size || 14) }}>{s.headline}</div>
      {s.sub && <div className="igr-in text-white/90 font-medium mt-[4cqw]" style={{ ...d(0.5), ...fs(5) }}>{s.sub}</div>}
      <div className="igr-pop igr-pulse bg-white text-[#0e0e0e] font-black igr-mono flex items-center gap-[2cqw] px-[6cqw] py-[4cqw] mt-[9cqw]" style={{ ...d(0.9), ...fs(4.6) }}>
        camp-compass.uk <ArrowRight style={{ width: "5cqw", height: "5cqw" }} />
      </div>
      <div className="igr-in igr-mono text-white/85 mt-[5cqw]" style={{ ...d(1.3), ...fs(3.2) }}>{s.footnote || "Free · 60 seconds · no card"}</div>
    </div>
  </div>
);

export const SCENES = {
  hook: HookScene, price: PriceScene, list: ListScene, counter: CounterScene,
  dots: DotsScene, map: MapScene, chat: ChatScene, photos: PhotosScene, cta: CtaScene,
};
